import { ActionsUnion, createAction } from '@housinganywhere/safe-redux';
import chunk from 'lodash-es/chunk';
import flatten from 'lodash-es/flatten';

import { Thunk } from 'ha/myredux/types';
import { Language } from '@ha/intl';
import { reportError } from 'ha/helpers/bugsnag/reportError';
import { ListingStateFilter } from 'ha/constants/Listing';
import { getIsNorthAmericanCountryCode } from 'ha/helpers/getIsNorthAmericanCountryCode';
import { CitiesSummary } from '@ha/api/v2/types/ListingsSummary';

import { makeAlgoliaQuery } from '../utils/makeAlgoliaQuery';
import { getGroupedListings } from '../utils/getGroupedListings';
import { ListingsGroup } from '../types';

export enum ActionTypes {
  START = '[advertiser listings] LOAD_ADVERTISER_LISTINGS_START',
  DONE = '[advertiser listings] LOAD_ADVERTISER_LISTINGS_DONE',
  FAIL = '[advertiser listings] LOAD_ADVERTISER_LISTINGS_ERROR',
}

export const Actions = {
  start: () => createAction(ActionTypes.START),
  done: (listingsGroup: ListingsGroup[]) =>
    createAction(ActionTypes.DONE, listingsGroup),
  fail: () => createAction(ActionTypes.FAIL),
};

export type ActionsType = ActionsUnion<typeof Actions>;

const AlgoliaQueriesLimit = 50;

const getIsImperialSystem = (citiesSummary: CitiesSummary[]) => {
  if (citiesSummary.length > 0) {
    return getIsNorthAmericanCountryCode(citiesSummary[0].countryCode);
  }
  return false;
};

const loadListings =
  (userId: number, lang?: Language): Thunk =>
  (dispatch, _, { apiV2, algolia }) => {
    dispatch(Actions.start());

    return apiV2
      .getListingsSummary({
        userId,
        withListingState: String(ListingStateFilter.ALL),
        lang,
      })
      .then(({ data: { cities } }) => cities)
      .then(citiesSummary => {
        const algoliaQueries = citiesSummary.map(c =>
          makeAlgoliaQuery({
            advertiserId: userId,
            country: c.country,
            city: c.city,
          }),
        );
        const isImperialSystem = getIsImperialSystem(citiesSummary);
        return Promise.all(
          chunk(algoliaQueries, AlgoliaQueriesLimit).map(queries =>
            algolia.searchListings(queries),
          ),
        ).then(responses => {
          const groupedListingsByCity = getGroupedListings(
            flatten(responses.map(res => res.results)),
            citiesSummary,
            isImperialSystem,
          );

          dispatch(Actions.done(groupedListingsByCity));
        });
      })
      .catch(error => {
        dispatch(Actions.fail());
        reportError('Algolia request failed', { metaData: { error } });
      });
  };

export { loadListings };
